import { CreateBendInputMode, CreateEdgeInputMode, EventRecognizers, GraphEditorInputMode, GraphItemTypes, GraphSnapContext, GridSnapTypes, HandleInputMode, ILabel, INode, ItemHoverInputMode, LassoSelectionInputMode, MarqueeSelectionInputMode, MouseEventRecognizers, MoveInputMode, MoveLabelInputMode, MoveViewportInputMode, OrthogonalEdgeEditingContext, OrthogonalEdgeEditingPolicy, PortDropInputMode, PortLayerPolicy, ShowPortCandidates, TouchEventRecognizers } from "yfiles";
import GroupPortCandidateProvider from "./groupPortCandidateProvider";
import RegularPortCandidateProvider from "./regularPortCandidateProvider";
import resizeGroupNodeDecorators from "./resizeGroupNodeDecorators";
let currentHovered = null;
let creatingEdge = false;
let marqueeSelectionProcess = false;
export const tryGetCurrentHoveredNode = () => {
    if (!currentHovered)
        return undefined;
    if (currentHovered instanceof INode)
        return currentHovered;
    if (currentHovered instanceof ILabel && currentHovered.owner instanceof INode)
        return currentHovered.owner;
    return undefined;
};
export default (graphComponentRef, canEdit, canMove, activeIconWithClickStopPropagationRef) => {
    if (!graphComponentRef.current)
        return undefined;
    const mode = new GraphEditorInputMode({
        allowGroupingOperations: canEdit,
        allowUndoOperations: canEdit,
        allowAddLabel: false,
        allowCreateBend: false,
        allowCreateNode: false,
        allowClearSelection: true,
        allowEditLabel: false,
        allowClipboardOperations: false,
        allowDuplicate: false,
        allowPaste: false,
        allowReparentNodes: false,
        allowReparentToNonGroupNodes: false,
        allowReverseEdge: false,
        allowGroupSelection: true,
        allowUngroupSelection: true,
        allowAdjustGroupNodeSize: false,
        allowEditLabelOnDoubleClick: false,
        orthogonalBendRemoval: OrthogonalEdgeEditingPolicy.AUTO,
        focusableItems: GraphItemTypes.NONE,
        movableItems: canMove
            ? GraphItemTypes.NODE | GraphItemTypes.BEND | GraphItemTypes.EDGE
            : GraphItemTypes.NONE,
        clickableItems: GraphItemTypes.NODE | GraphItemTypes.BEND | GraphItemTypes.EDGE,
        selectableItems: GraphItemTypes.NODE | GraphItemTypes.BEND | GraphItemTypes.EDGE,
        clickSelectableItems: GraphItemTypes.NODE | GraphItemTypes.BEND | GraphItemTypes.EDGE,
        selectablePredicate: (obj) => {
            if (activeIconWithClickStopPropagationRef === null || activeIconWithClickStopPropagationRef === void 0 ? void 0 : activeIconWithClickStopPropagationRef.current)
                return false;
            if (marqueeSelectionProcess && !(obj instanceof INode))
                return false;
            const node = tryGetCurrentHoveredNode();
            return Boolean(!node || node === obj);
        },
        deletableItems: canEdit ? GraphItemTypes.NODE | GraphItemTypes.EDGE : GraphItemTypes.NONE,
        moveLabelInputMode: new MoveLabelInputMode({
            enabled: false
        }),
        portDropInputMode: new PortDropInputMode({
            enabled: false
        }),
        showHandleItems: GraphItemTypes.ALL,
        orthogonalEdgeEditingContext: new OrthogonalEdgeEditingContext({
            enabled: false,
            movePorts: true
        }),
        allowCreateEdge: canEdit,
        createEdgeInputMode: new CreateEdgeInputMode({
            enabled: canEdit,
            snapDistance: 5,
            allowCreateBend: true,
            allowSelfloops: false,
            allowEdgeToEdgeConnections: false,
            showPortCandidates: ShowPortCandidates.NONE,
            snapToTargetCandidate: true,
            startOverCandidateOnly: false,
            useHitItemsCandidatesOnly: false,
            cancelGestureOnInvalidTarget: true,
            showTargetHighlight: false,
            prepareRecognizer: EventRecognizers.createAndRecognizer(MouseEventRecognizers.LEFT_DOWN, () => {
                var _a;
                marqueeSelectionProcess = false;
                const node = tryGetCurrentHoveredNode();
                if (!((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.graph) ||
                    !node ||
                    graphComponentRef.current.graph.isGroupNode(node))
                    return false;
                creatingEdge = !graphComponentRef.current.selection.isSelected(node);
                return creatingEdge;
            }),
            finishRecognizer: EventRecognizers.createAndRecognizer(MouseEventRecognizers.LEFT_UP, () => {
                creatingEdge = false;
                return true;
            })
        }),
        itemHoverInputMode: new ItemHoverInputMode({
            enabled: true,
            hoverItems: GraphItemTypes.NODE | GraphItemTypes.NODE_LABEL | GraphItemTypes.EDGE,
            discardInvalidItems: true
        }),
        moveInputMode: new MoveInputMode({
            enabled: canMove
        }),
        moveUnselectedInputMode: new MoveInputMode({
            enabled: false
        }),
        moveViewportInputMode: new MoveViewportInputMode({
            enabled: true,
            allowSinglePointerMovement: true,
            validBeginRecognizer: EventRecognizers.createAndRecognizer(MouseEventRecognizers.LEFT_DOWN, () => {
                var _a;
                marqueeSelectionProcess = false;
                if (!((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.selection.selectedNodes.size))
                    return true;
                creatingEdge = false;
                return true;
            }),
            draggedRecognizer: MouseEventRecognizers.LEFT_DRAG,
            pressedRecognizer: EventRecognizers.createAndRecognizer(MouseEventRecognizers.LEFT_DRAG, () => {
                var _a, _b;
                if (!((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.selection.selectedNodes.size))
                    return true;
                const node = tryGetCurrentHoveredNode();
                if (!node)
                    return true;
                creatingEdge = !((_b = graphComponentRef.current) === null || _b === void 0 ? void 0 : _b.selection.isSelected(node));
                return creatingEdge;
            })
        }),
        marqueeSelectionInputMode: new MarqueeSelectionInputMode({
            enabled: true,
            draggedRecognizer: MouseEventRecognizers.RIGHT_DRAG,
            validBeginRecognizer: EventRecognizers.createAndRecognizer(MouseEventRecognizers.RIGHT_DOWN, () => {
                marqueeSelectionProcess = true;
                return true;
            }),
            pressedRecognizer: MouseEventRecognizers.RIGHT_DRAG,
            cancelRecognizer: EventRecognizers.createAndRecognizer(MouseEventRecognizers.LEAVE, () => {
                marqueeSelectionProcess = false;
                return true;
            }),
            releasedRecognizer: MouseEventRecognizers.RIGHT_UP
        }),
        lassoSelectionInputMode: new LassoSelectionInputMode({
            enabled: false
        }),
        handleInputMode: new HandleInputMode({
            enabled: true
        }),
        createBendInputMode: new CreateBendInputMode({
            enabled: canEdit,
            prepareRecognizerTouch: TouchEventRecognizers.TOUCH_DOWN_PRIMARY
        }),
        snapContext: new GraphSnapContext({
            enabled: true,
            collectEdgeSnapLines: true,
            cropSnapLines: false,
            gridSnapType: GridSnapTypes.NONE,
            snapBendsToSnapLines: false,
            snapBendAdjacentSegments: true,
            collectNodePairCenterSnapLines: false,
            collectNodePairSegmentSnapLines: false,
            collectNodeSnapLines: false,
            collectNodePairSnapLines: false,
            collectPortSnapLines: true,
            snapNodesToSnapLines: false,
            snapOrthogonalMovement: true,
            snapPortAdjacentSegments: true,
            snapSegmentsToSnapLines: true,
            visualizeSnapResults: false,
            collectNodeSizes: true,
            gridSnapDistance: 10,
            edgeToEdgeDistance: 10,
            nodeToEdgeDistance: 10,
            nodeToNodeDistance: 10
        })
    });
    graphComponentRef.current.graphModelManager.portLayerPolicy = PortLayerPolicy.SEPARATE_LAYER;
    mode.itemHoverInputMode.addHoveredItemChangedListener((_, evt) => {
        currentHovered = evt.item;
    });
    graphComponentRef.current.graph.decorator.nodeDecorator.portCandidateProviderDecorator.setFactory(node => {
        var _a;
        return ((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.graph.isGroupNode(node))
            ? new GroupPortCandidateProvider(node)
            : new RegularPortCandidateProvider(node);
    });
    resizeGroupNodeDecorators(graphComponentRef, canEdit);
    graphComponentRef.current.inputMode = mode;
    return mode;
};
